import React, { useEffect, useMemo, useRef } from 'react';
import { OrgChart } from 'd3-org-chart';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useGetEmployeeTreeQuery } from '../../redux/apis/employee';
import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import { Skeleton } from '../../components/UI/Skeleton/Skeleton';

import './OrgChart.scss';

/* Wraps a control with a styled hover/focus tooltip. */
const WithTooltip = ({ id, text, children }) => (
  <OverlayTrigger
    placement="bottom"
    delay={{ show: 150, hide: 0 }}
    overlay={
      <Tooltip id={id} className="org-tree-tooltip">
        {text}
      </Tooltip>
    }
  >
    {children}
  </OverlayTrigger>
);

/* Skeleton placeholder shaped like the org chart: a root node, connector
   lines, and a row of child node cards. Uses the shared shimmer. */
const OrgNodeSkeleton = () => (
  <div className="org-tree-skeleton__node">
    <Skeleton variant="circle" width={44} height={44} className="org-tree-skeleton__avatar" />
    <div className="org-tree-skeleton__lines">
      <Skeleton variant="text" width="70%" />
      <Skeleton variant="text" width="90%" height={9} />
      <Skeleton variant="text" width="45%" height={9} />
    </div>
  </div>
);

const OrgTreeSkeleton = () => (
  <div className="org-tree-skeleton" role="status" aria-live="polite" aria-busy="true">
    <span className="visually-hidden">Loading organization chart…</span>
    <div className="org-tree-skeleton__root">
      <OrgNodeSkeleton />
    </div>
    <span className="org-tree-skeleton__trunk" aria-hidden />
    <div className="org-tree-skeleton__row">
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="org-tree-skeleton__branch" key={i}>
          <span className="org-tree-skeleton__connector" aria-hidden />
          <OrgNodeSkeleton />
          {i % 2 === 0 ? (
            <>
              <span
                className="org-tree-skeleton__connector org-tree-skeleton__connector--short"
                aria-hidden
              />
              <OrgNodeSkeleton />
            </>
          ) : null}
        </div>
      ))}
    </div>
  </div>
);

const OrgChartComponent = ({ userId = '20' }) => {
  const { data: loginData } = useIsLoggedInQuery();

  const chartRef = useRef(null);
  const containerRef = useRef(null);

  const { data, isLoading, isError, refetch } = useGetEmployeeTreeQuery(userId);

  useEffect(() => {
    refetch();
  }, []);

  const treeData = data?.data;

  const hasValidTreeData = useMemo(() => {
    if (!treeData) return false;
    if (Array.isArray(treeData)) {
      return treeData.some((node) => node?.id != null);
    }
    if (typeof treeData === 'object') {
      if (Object.keys(treeData).length === 0) return false;
      return treeData.id != null;
    }
    return false;
  }, [treeData]);

  // 🔹 Flatten API tree → d3-org-chart format
  const flattenTree = (node, result, parentId = null) => {
    if (node?.id == null) return;

    result.push({
      id: node.id.toString(),
      parentId: parentId != null ? parentId.toString() : null,
      name: node.name || '',
      email: node.email || '',
      role: node.hrxRole || '',
      image: node.profilePicture || null
    });

    if (Array.isArray(node.children)) {
      node.children.forEach((child) => flattenTree(child, result, node.id));
    }
  };

  const centerId = useMemo(
    () => loginData?.data?._id?.toString() || userId?.toString(),
    [loginData, userId]
  );

  useEffect(() => {
    if (!hasValidTreeData || !containerRef.current) return;

    const flatData = [];
    const roots = Array.isArray(treeData) ? treeData : [treeData];

    // 1️⃣ Flatten tree correctly
    roots.forEach((root) => flattenTree(root, flatData));

    if (!flatData.length) return;

    // 2️⃣ SANITIZE parentId (VERY IMPORTANT)
    const idSet = new Set(flatData.map((n) => n.id));

    flatData.forEach((n) => {
      // normalize undefined / empty parentId
      if (n.parentId === undefined || n.parentId === '') {
        n.parentId = null;
      }

      // orphan protection
      if (n.parentId && !idSet.has(n.parentId)) {
        n.parentId = null;
      }
    });

    // 3️⃣ ENSURE SINGLE ROOT (d3 requirement)
    const rootNodes = flatData.filter((n) => n.parentId === null);

    if (rootNodes.length > 1) {
      const virtualRootId = '__root__';

      rootNodes.forEach((n) => {
        n.parentId = virtualRootId;
      });

      flatData.unshift({
        id: virtualRootId,
        parentId: null,
        name: 'Organization',
        email: '',
        role: '',
        image: null
      });
    }

    // 4️⃣ Init chart once
    if (!chartRef.current) {
      chartRef.current = new OrgChart();
    }

    const validCenterNode = flatData.find((n) => n.id === centerId);

    // 5️⃣ Render chart (UNCHANGED)
    chartRef.current
      .container(containerRef.current)
      .data(flatData)
      .nodeWidth(() => 260)
      .nodeHeight(() => 150)
      .childrenMargin(() => 50)
      .compactMarginBetween(() => 35)
      .compactMarginPair(() => 40)
      .nodeContent((d) => {
        const isYou = d.data.id === centerId;

        const img =
          d.data.image ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(
            d.data.name
          )}&background=E5E7EB&color=111827`;

        return `
        <div style="width:${d.width}px;height:${d.height}px;padding-top:32px;">
          <div style="
            background:#ffffff;
            border-radius:14px;
            height:100%;
            border:${isYou ? '2px solid #4F46E5' : '1px solid #E5E7EB'};
            box-shadow:0 10px 24px rgba(0,0,0,0.08);
            font-family:Inter, system-ui;
            position:relative;
          ">

            ${
              isYou
                ? `
              <div style="
                position:absolute;
                top:10px;
                right:12px;
                background:#4F46E5;
                color:#fff;
                font-size:10px;
                padding:3px 8px;
                border-radius:999px;
                font-weight:600;
              ">YOU</div>
            `
                : ''
            }

            <div style="
              position:absolute;
              top:-24px;
              left:20px;
              background:#fff;
              border-radius:50%;
              padding:3px;
              border:${isYou ? '2px solid #4F46E5' : '1px solid #E5E7EB'};
            ">
              <img src="${img}" style="
                width:48px;
                height:48px;
                border-radius:50%;
                object-fit:cover;
              " />
            </div>

            <div style="padding:40px 16px 16px">
              <div style="font-size:15px;font-weight:600;color:#111827;">
                ${d.data.name}
              </div>

              <div style="
                font-size:12px;
                color:#4B5563;
                margin-top:5px;
                word-break:break-all;
              ">
                ${d.data.email}
              </div>

              <div style="font-size:12px;color:#6B7280;margin-top:4px;">
                ${d.data.role}
              </div>
            </div>
          </div>
        </div>
      `;
      })
      .render()
      .expandAll();

    if (validCenterNode) {
      chartRef.current.setCentered(validCenterNode.id);
    }

    chartRef.current.fit();
  }, [hasValidTreeData, treeData, centerId]);

  const handleFit = () => chartRef.current?.fit?.();
  const handleExpandAll = () => chartRef.current?.expandAll?.();
  const handleCollapseAll = () => chartRef.current?.collapseAll?.();
  const handleCenterMe = () => {
    if (!centerId) return;
    chartRef.current?.setCentered?.(centerId);
    chartRef.current?.fit?.();
  };

  if (isLoading)
    return (
      <div className="org-tree-page org-tree-page--loading">
        <div className="org-tree-page__header">
          <div>
            <h2 className="org-tree-page__title">Organization Tree</h2>
            <p className="org-tree-page__subtitle">Explore reporting lines and team structure.</p>
          </div>
          <div className="org-tree-page__actions" aria-hidden>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} width={92} height={40} style={{ borderRadius: 12 }} />
            ))}
          </div>
        </div>
        <div className="org-tree-surface">
          <OrgTreeSkeleton />
        </div>
      </div>
    );

  if (isError)
    return (
      <div className="org-tree-page org-tree-page--error">
        <div className="org-tree-surface org-tree-surface--center">
          <div className="org-tree-empty">
            <div className="org-tree-empty__icon" aria-hidden>
              <i className="icofont-warning-alt" />
            </div>
            <div className="org-tree-empty__title">Unable to load organization chart</div>
            <div className="org-tree-empty__desc">
              Something went wrong while fetching the organization data. Please try again.
            </div>
            <button type="button" className="org-tree-btn org-tree-btn--primary" onClick={refetch}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );

  if (!hasValidTreeData)
    return (
      <div className="org-tree-page org-tree-page--empty">
        <div className="org-tree-page__header">
          <div>
            <h2 className="org-tree-page__title">Organization Tree</h2>
            <p className="org-tree-page__subtitle">Explore reporting lines and team structure.</p>
          </div>
        </div>
        <div className="org-tree-surface org-tree-surface--center">
          <div className="org-tree-empty">
            <div className="org-tree-empty__icon" aria-hidden>
              <i className="icofont-people" />
            </div>
            <div className="org-tree-empty__title">No organization data</div>
            <div className="org-tree-empty__desc">
              There is no organization chart to display for this employee yet.
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="org-tree-page">
      <div className="org-tree-page__header">
        <div>
          <h2 className="org-tree-page__title">Organization Tree</h2>
          <p className="org-tree-page__subtitle">Explore reporting lines and team structure.</p>
        </div>

        <div
          className="org-tree-page__actions"
          role="toolbar"
          aria-label="Organization tree actions"
        >
          <WithTooltip id="org-tree-center-tip" text="Center the chart on you">
            <button
              type="button"
              className="org-tree-btn org-tree-btn--primary"
              onClick={handleCenterMe}
            >
              <i className="icofont-location-pin" aria-hidden /> Center on me
            </button>
          </WithTooltip>
          <WithTooltip id="org-tree-fit-tip" text="Fit the whole chart to screen">
            <button type="button" className="org-tree-btn" onClick={handleFit}>
              <i className="icofont-full-screen" aria-hidden /> Fit
            </button>
          </WithTooltip>
          <WithTooltip id="org-tree-expand-tip" text="Expand all nodes">
            <button type="button" className="org-tree-btn" onClick={handleExpandAll}>
              <i className="icofont-plus-circle" aria-hidden /> Expand
            </button>
          </WithTooltip>
          <WithTooltip id="org-tree-collapse-tip" text="Collapse all nodes">
            <button type="button" className="org-tree-btn" onClick={handleCollapseAll}>
              <i className="icofont-minus-circle" aria-hidden /> Collapse
            </button>
          </WithTooltip>
        </div>
      </div>

      <div className="org-tree-surface">
        <div ref={containerRef} className="org-tree-canvas" />
      </div>
    </div>
  );
};

export default OrgChartComponent;
